import {Page} from "../page"

export const socialFeeds: Page = {
    uri: 'learn/social-feeds',
    icon: '📱',
    title: $localize`:@@page-social-feeds-title:Social & Feeds`,
    seoTitle: $localize`:@@page-social-feeds-seo-title:Social feeds — posts, photos and auto-translation`,
    summary: $localize`:@@page-social-feeds-summary:Two activity feeds — For you and Following — to discover games, events, and people, with automatic translation across languages.`,
    markdownContent: [
        $localize`:@@page-social-feeds-the-feeds:Ludoya has two main activity feeds that help you discover games, events, and people. Each feed shows different content and serves a different purpose.

## The Feeds

### Following Feed

Your personalized stream of activity from the people and groups you follow. **Shows everything they share — no filtering.** Posts, play logs, reviews, event announcements, collection changes, all of it, in full. Infinite scroll to catch up on what's new from the people you actually care about.

![The Following feed: everything the people and groups you follow share — plays, a shared Game Kit, and a club's event](/assets/screenshots/social-feeds-following.webp)

### For You

Activity from the entire Ludoya community, curated to show only **relevant posts** — those with photos or real text content — and ranked by how recent they are, how much people engage with them, and how close they happened to you, so a game night down the road rises above one on another continent. It's the first tab, and the one you see before you sign in or follow anyone.

![The For you feed: plays with table photos from the whole community](/assets/screenshots/social-feeds-global.webp)`,

        $localize`:@@page-social-feeds-automatic-translation:## Automatic Translation

Board gaming is global, and Ludoya's feed doesn't stop at your language. Posts written in a language you don't speak are **translated for you automatically** — no button, no setup. You just read them.

A translated post is labelled **"Translated from [language]"**, with **Show original** if you want the writer's own words.

**Languages you already understand are left alone.** Tap the gear next to the translation label and switch on **Don't auto-translate from [language]** — from then on, posts in that language stay as written. Should you ever want one translated anyway, a **Show translation** button is right there. You can manage the full list of languages you understand in **Settings → Language**.

This applies across the feed: posts, event announcements, play notes, and comments.

![Language settings: the languages you understand are never auto-translated](/assets/screenshots/settings-language.webp)`,

        $localize`:@@page-social-feeds-content-types-in-feeds:## Content Types in Feeds

Any of the following can show up as a feed item, each rendered with its own banner so you can see at a glance what it is:

### Posts

Text, photos. Share what you're thinking, coordinate game nights, ask for recommendations, or just chat. Posts can have photos and links, and may be tied to a specific game.

### Reviews

Game [reviews](/learn/reviews) from community members, shown with the game and its star rating. Helpful for deciding what to play next or reading multiple perspectives on a game you're considering.

### Plays

When you or your friends [log a game session](/learn/play-logging) — whether through [quick logging](/learn/play-logging) or a [live session](/learn/live-sessions) — it shows up as a feed item with the game, the players, and any photos or comments captured during play. Tap to see full details.

### Collection Changes

See when someone adds or removes a game from their [collection](/learn/collection) — new acquisitions, wishlist additions, games sold off. Discover what people are excited about.

### Events

New [events](/learn/events) appear in the feed with the title, date, scheduled game count, and how many people are going. Great for finding things to join in your region.

### Scheduled Games

[Scheduled games](/learn/scheduled-games) — both standalone ones and those nested inside a parent event — show up with their date, seat count, and how many seats are filled.

### Marketplace Listings

New [marketplace](/learn/marketplace) listings appear in the feed with the game, listing type (Sell / Trade / Sell-or-Trade), price, condition, and location. A quick way to spot deals and trades in your region.

### Game Kits

When someone publishes a [Game Kit](/learn/game-kits) — a reusable play template with roles, setup steps, round steps, and a structured score sheet — it surfaces in the feed with quick stats about the kit's contents and how many times it's been used.

### Campaigns

Long-running [campaigns](/learn/campaigns) appear as their owners open them up, so you can follow a group's legacy game or RPG as it unfolds.

### Shop Deals & New Arrivals

Price drops and fresh stock from retail shops land in the feed too, marked with a **% off** badge or a **New** flag. See [Shops & Prices](/learn/shops-prices).`,

        $localize`:@@page-social-feeds-interacting-with-content:## Interacting with Content

### Like

Tap the heart icon to like a post, play log, or review. Your like is visible to others.

### Comment

Tap the comment icon to add a comment. Start conversations about plays, games, or anything shared. Replies are threaded so you can follow the conversation.

### Reply

Within a comment thread, tap **Reply** to respond directly to someone. They'll be notified, and the notification opens the thread at their reply. Replies nest under the comment they answer, so a long discussion stays readable. Deleting a comment that has replies keeps the replies: the comment shows as deleted and the conversation under it stays.

### Edit and Format

Made a typo? **Edit** your post from its menu, photos and video included. The box you write posts and comments in has a small toolbar for **bold**, *italic* and bullet lists.

### Menus Everywhere

Right-click a post, a comment, a game or a play on a computer, or long-press it on a phone, to open its actions right there. Long-press the heart to see who liked something.

### Mention Someone

Type **@** while writing a post or comment and pick a name from the suggestions — they get a notification and their name becomes a link. Comments can carry images too.

### Pin Your Best Post

Pin one of your own posts to the top of your profile from its **⋮** menu, and swap it whenever you like. Group admins can do the same on a group's profile.

### Delete, Report, Block

The same **⋮** menu lets you delete your own posts, and — on anyone else's — **Report** it or **Block** the author. See [Privacy & Visibility](/learn/privacy-visibility).`,

        $localize`:@@page-social-feeds-formatting-your-text:## Formatting Your Text

Posts and comments, event, club and campaign descriptions, profile bios, marketplace listings, play notes and game kit texts share one small toolbar above the box: a heading, **bold**, *italic*, a bulleted list and a link. Select some words and press a button to format them; a second press takes a heading, bold, italic or list off again. **Ctrl+B**, **Ctrl+I** and **Ctrl+K** (⌘ on a Mac) do the same, and **Preview** shows how the text will read before you post or save it.

A pasted web address becomes a link on its own; the link button is for when you want words to carry it instead, like \`[our website](https://…)\`.`,

        $localize`:@@page-social-feeds-schedule-a-post:## Schedule a Post, a Review or an Event

Write something now and have it go out later: an announcement for the morning, a reminder the day before your game night, a club's weekly news, a review copy's review on its embargo date, next month's event on the first of the month.

1. Write the post as usual: text, photos, a video or a poll, in a group if you are posting to one.
2. Tap **Schedule…** next to **Publish** and pick the day and time. Under the fields Ludoya spells out when it goes out, weekday and time zone included. It can be a few minutes from now or up to a year ahead, and next time it suggests the time of day you picked last.
3. Tap **Schedule**.

Until its time the post is not public: only you see it (and, for a post in a group, that group's admins). It goes out on its own within a minute of the time you picked, and lands at the top of the feeds like any new post. The people you mention and the group's admins hear about it then, not before, and you get a notification that it is out.

**A review** works the same way, the first time you review a game: in the review form's **More** tab, **Schedule…** under **When it goes out**. Until then nobody else sees it and it doesn't count in the game's rating. Opening the review form on that game again edits the scheduled one, its time included. A review that is already out changes at once.

**A club's event** you saved as a draft can go live by itself: in the event's console, **Publish on…** beside **Publish** opens **Publish it by itself**, where you pick a day and time before the event starts. At that time it is announced and opens for sign-ups, just as when you tap **Publish**; until then it stays a draft, so you can keep editing it, and only those who run it see it. If its programme holds drafts, they go out with it. The button then shows the time, and tapping it moves the time or keeps it a draft; the console's **Needs attention** and the event's page say when it goes live too. Publishing it by hand before then is fine.

**Scheduled** lists everything waiting, soonest first. Open it from **More** in the side menu, from **N scheduled** at the top of the post editor, or from the message right after scheduling. For each one you can:

- **Edit** a post (its text, photos, video, game, poll and time; only its group stays as it is) or a review (all of it), or **Open** an event in its console. Saving a post or a review keeps it scheduled (its button says until when), and its form also has **Publish now instead** and **Delete**.
- **Publish now**, after a confirmation, if it should go out straight away.
- **Delete** a post or a review: it is never published, and its photos, video and poll go with it. For an event, **Keep it a draft** drops its time and leaves the draft as it is.

If something cannot go out at its time (for example, you may no longer post in a post's group, you reviewed the game in the meantime, or an event's parent event is still a draft), it stays in Scheduled marked **Missed**, with the reason, and you get a notification. Edit it to pick a new time, or publish it once that is sorted. A missed review shows all of its text, with **Open your review** to copy what you need into the one that went out.

**In a club's console**, its admins see **N scheduled** among the quick actions, and a line in **Needs attention** when something missed its time: everyone's posts and the club's events waiting to go out, each saying whose it is. They can delete any post (its author is told) but only its author can change or publish one.`,

        $localize`:@@page-social-feeds-discovery-loops:## Discovery Loops

Ludoya is designed so discovery chains together naturally:

1. **See a friend's play** in your Following feed
2. **Tap the game** to see the game's page, reviews, and where to play it
3. **Find an event** listing that game
4. **Join the event** to play with your community

Each piece of content is a starting point for discovery.`,

        $localize`:@@page-social-feeds-scoped-feeds:## Scoped Feeds

Beyond the main feeds, activity also gets grouped into smaller, more focused streams:

### User Profile Feeds

Every [user profile](/learn/user-profiles) has its own activity feed showing just that person's contributions — their posts, plays, reviews, collection changes, events, scheduled games, marketplace listings, and game kits all in one stream. Visit a profile to browse someone's full Ludoya history without the noise of everyone else.

### Group Feeds

[Groups](/learn/groups) have their own activity feed separate from the main feeds. Members post, log plays, and share updates within the group. This keeps group conversation focused and organized.

![A group's profile with its own feed of events and plays](/assets/screenshots/group-page.webp)

### Pinned Posts

Group admins can pin a post to the top of the group's profile. Great for announcements, rules, or recurring logistics — it's one pin at a time, so it always points at what matters most right now.`,

        $localize`:@@page-social-feeds-sharing-beyond-ludoya:## Sharing Beyond Ludoya

### Image Exports

Share your gaming activity on social media and beyond:

- **Collection Export**: An image of your collection highlights (customizable—choose which games show)
- **Play History Export**: Visual summary of recent plays with scores
- **Monthly Summary**: Auto-generated card showing your gaming [stats](/learn/statistics) for the month
- **Event Card**: Share an event you're attending with details and your RSVP

Save these as images and post them anywhere.

### QR Codes

Share Ludoya experiences using QR codes:

- **Event QR**: Scan to RSVP to an event (great for posters and flyers)
- **Profile QR**: Scan to follow or view a profile
- **Game QR**: Scan to see a game's page and where to play it
- **Group QR**: Scan to request or join a group

Print these on posters, include in emails, or text them to friends.

### Deep Links

Shareable URLs that preserve context and filters:

- Share a filtered [play history](/learn/play-logging): the period, game and people you picked
- Share an Explore search, with its text and game tags

When friends tap the link, they see exactly what you're referencing.

### Embed Snippets

Put your collection, your plays or your upcoming events straight onto your own website or forum. Open a collection and choose **Create Web Embed** for a ready-made snippet that keeps whatever filter you're looking at; plays and events are addresses you point an iframe at yourself. [Integrations](/learn/integrations) has the details.

![An embedded collection on an external website](/assets/screenshots/embed-collection.webp)`,

        $localize`:@@page-social-feeds-section-8:**See also:**
- [User Profiles](/learn/user-profiles) — customizable profile layouts
- [Reviews](/learn/reviews) — sharing game opinions
- [Events](/learn/events) — hosting and joining game sessions
- [Play Logging](/learn/play-logging) — recording games you've played
- [Groups](/learn/groups) — organizing community spaces`,
    ].join('\n\n---\n\n')
}
